import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { reguasApi, GATILHOS_SUPORTADOS, type Regua } from '@/lib/api/reguas';
import { adminApi, type ConfiguracoesMandato } from '@/lib/api/admin';
import { contatosApi } from '@/lib/api/contatos';
import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from '@/lib/stores/toastStore';
import { Drawer } from '@/design-system/Drawer';
import { Info } from '@/design-system/Info';

const rotuloGatilho = (g: string) => GATILHOS_SUPORTADOS.find((x) => x.v === g)?.l || g;
const suportado = (g: string) => GATILHOS_SUPORTADOS.some((x) => x.v === g);

/**
 * `configuracoes` é um jsonb livre (hoje só usado para `{ canal: 'whatsapp' }`
 * — ver regua.entity.ts). Não há formulário nem endpoint que grave público-alvo
 * ou modelo aprovado na Meta; se algum dia existir, esta tela já lê de lá em
 * vez de inventar o valor.
 */
const publicoDe = (r: Regua) => (r.configuracoes as any)?.publico || '—';
const modeloMetaDe = (r: Regua) => (r.configuracoes as any)?.template_meta_id || '—';

export function ReguasPage() {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();

  const [modal, setModal] = useState<Regua | 'nova' | null>(null);

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const { data: reguas = [], isLoading } = useQuery({
    queryKey: ['reguas'],
    queryFn: () => reguasApi.list(token),
    enabled: !!token,
  });

  const { data: mandato } = useQuery({
    queryKey: ['mandato'],
    queryFn: () => adminApi.getMandato(token),
    enabled: !!token,
  });

  // Opt-outs: dado real (COUNT de contatos com base_legal='optout'), mas com
  // escopo diferente do que o protótipo promete ("causados" em 30d por
  // régua): a API de contatos não guarda qual régua ou campanha levou ao
  // opt-out, nem filtra por data. Não dá para fingir essa granularidade —
  // ver nota no card abaixo.
  const { data: optouts } = useQuery({
    queryKey: ['reguas-optouts'],
    queryFn: () => contatosApi.list({ base_legal: 'optout', limit: 1 }, token),
    enabled: !!token,
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['reguas'] });

  const criar = useMutation({
    mutationFn: (dados: any) => reguasApi.create(dados, token),
    onSuccess: () => {
      invalidar();
      toast('Régua criada, desativada por padrão. Revise a mensagem antes de ligar.');
      setModal(null);
    },
    // O servidor recusa duas réguas no mesmo gatilho
    onError: (err: any) => toast(erroDe(err, 'Erro ao criar a régua.')),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<Regua> }) =>
      reguasApi.update(id, dados, token),
    onSuccess: (r) => {
      invalidar();
      toast(`Régua "${r.nome}" ${r.ativa ? 'ativada' : 'desativada'}.`);
      setModal(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar a régua.')),
  });

  const excluir = useMutation({
    mutationFn: (id: string) => reguasApi.delete(id, token),
    onSuccess: () => {
      invalidar();
      toast('Régua removida.');
      setModal(null);
    },
    // O servidor exige desativar antes de excluir
    onError: (err: any) => toast(erroDe(err, 'Erro ao remover a régua.')),
  });

  const config = mandato?.configuracoes;

  return (
    <div className="page" id="p-reguas">
      <div className="head">
        <div>
          <h1>Réguas de relacionamento</h1>
          <p>
            {/*
              proto:733 fecha com "calendário eleitoral", que não existe: o
              único bloqueio é a chave de ambiente
              `BLOQUEIO_PERIODO_ELEITORAL`, conferida só na CRIAÇÃO de campanha
              — não na aprovação e não no worker. As outras três garantias são
              aplicadas no instante do disparo, em `outbox.worker.ts`.
            */}
            Disparos automáticos de WhatsApp com gatilho, público e limite de frequência; o mesmo
            gatilho não pode ter duas réguas. Todo envio passa pelo mesmo despachante: opt-in
            conferido no disparo, janela de silêncio e teto mensal por pessoa.
          </p>
        </div>
        <div className="act">
          <button className="btn pri" onClick={() => setModal('nova')}>
            + Nova régua
          </button>
        </div>
      </div>

      {/* KPIs de conformidade (proto:734). Onde a API não fornece o dado
          real, a tela declara a lacuna em vez de inventar um número — ver
          comentários em cada card. */}
      <div className="grid g4">
        <div className="card kpi">
          <div className="l">Mensagens automáticas · 30d</div>
          {/* Só a régua do gatilho 'solicitacao.concluida' dispara de fato
              hoje (SolicitacoesService.triggerFeedbackRegua grava em
              gab.campanha_msg com campanha_id='regua_automatica'), mas não
              existe endpoint que some ou liste esses envios por régua/período
              — ver GET /gab/campanhas (lista Campanha, não CampanhaMsg) e
              GET /gab/campanhas/:id (não encontraria 'regua_automatica').
              Um "0" aqui pareceria monitoramento; a lacuna é a informação
              honesta. */}
          <div className="v" style={{ fontSize: 15, lineHeight: 1.3, color: 'var(--ink3)' }}>
            Não medido
          </div>
          <div className="d">a API não expõe envios nem respostas por régua hoje</div>
        </div>
        <div className="card kpi">
          <div className="l">Opt-outs</div>
          <div className="v">{optouts?.total ?? '—'}</div>
          <div className="d">
            total de contatos em opt-out · não segmentado por régua nem por período
          </div>
        </div>
        <div className="card kpi">
          {/*
            O ícone estava no `<h1>`, e de lá acrescentava um "i" ao nome da
            tela: "Réguas de relacionamento i" -- lido assim por leitor de
            tela e comparado assim com o título do protótipo. Aqui, além de
            não ter esse efeito, ele fica ANCORADO no número que explica: são
            estes o teto e o silêncio de que a nota fala.
          */}
          <div className="l">
            Teto por pessoa
            <Info titulo="Onde o teto e o silêncio são configurados">
              Teto por contato, janela de silêncio e limiar de aprovação ficam em{' '}
              <b>Admin › Limiares</b> e valem para todas as réguas. Contatos sem opt-in nunca
              recebem, seja qual for a régua.
            </Info>
          </div>
          <div className="v">
            {config?.teto_mensagens_mes ?? '—'} <span style={{ fontSize: 14, color: 'var(--ink3)' }}>/mês</span>
          </div>
          <div className="d">
            silêncio {config?.silencio_inicio ?? '—'}–{config?.silencio_fim ?? '—'}
          </div>
        </div>
        <div className="card kpi">
          <div className="l">Calendário eleitoral</div>
          {/* Existe um bloqueio de defeso eleitoral no backend
              (CampanhasService.create, flag BLOQUEIO_PERIODO_ELEITORAL), mas
              ele vale para campanhas, não para réguas, e não há endpoint que
              devolva esse status para a tela ler. "Fora do defeso" seria uma
              garantia que ninguém verificou. */}
          <div className="v" style={{ fontSize: 15, lineHeight: 1.3, color: 'var(--ink3)' }}>
            Indisponível
          </div>
          <div className="d">a API não expõe o status do período eleitoral nesta tela</div>
        </div>
      </div>

      {/* Tabela (proto:735): Público e Modelo (Meta) só aparecem quando a
          régua tiver essa configuração salva em `configuracoes` — hoje
          nenhum fluxo grava esses campos, então a maioria das linhas mostra
          "—" em vez de um valor fabricado. */}
      <div className="card flat tw" style={{ marginTop: 12 }}>
        <table>
          <thead>
            <tr>
              <th>Régua</th>
              <th>Gatilho</th>
              <th>Público</th>
              <th>Modelo (Meta)</th>
              <th>30 dias</th>
              <th>Ativa</th>
            </tr>
          </thead>
          <tbody>
            {reguas.map((r) => (
              <tr key={r.id} className="row" onClick={() => setModal(r)}>
                <td>
                  <b>{r.nome}</b>
                  {!suportado(r.gatilho) && (
                    <span
                      className="chip warn"
                      style={{ fontSize: 10, marginLeft: 6 }}
                      title="A plataforma ainda não emite este evento"
                    >
                      gatilho não emitido
                    </span>
                  )}
                </td>
                <td>{rotuloGatilho(r.gatilho)}</td>
                <td>{publicoDe(r)}</td>
                <td className="mono">{modeloMetaDe(r)}</td>
                <td className="num" title="A API não expõe envios por régua ainda">
                  —
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <button
                    className={`tog ${r.ativa ? 'on' : ''}`}
                    style={{ cursor: 'pointer' }}
                    disabled={atualizar.isPending}
                    onClick={() => atualizar.mutate({ id: r.id, dados: { ativa: !r.ativa } })}
                  >
                    {r.ativa ? 'Ativa' : 'Inativa'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {isLoading && <div className="empty">Carregando réguas...</div>}
        {!isLoading && reguas.length === 0 && (
          <div className="empty">Nenhuma régua cadastrada.</div>
        )}
      </div>

      {modal && (
        <ModalRegua
          regua={modal === 'nova' ? null : modal}
          config={config}
          salvando={criar.isPending || atualizar.isPending}
          excluindo={excluir.isPending}
          onCancelar={() => setModal(null)}
          onSalvar={(dados) => {
            if (modal === 'nova') criar.mutate(dados);
            else atualizar.mutate({ id: modal.id, dados });
          }}
          onExcluir={
            modal === 'nova'
              ? undefined
              : () => {
                  if (window.confirm(`Remover a régua "${modal.nome}"?`)) excluir.mutate(modal.id);
                }
          }
        />
      )}
    </div>
  );
}

function ModalRegua({
  regua,
  config,
  salvando,
  excluindo,
  onCancelar,
  onSalvar,
  onExcluir,
}: {
  regua: Regua | null;
  config?: ConfiguracoesMandato;
  salvando: boolean;
  excluindo: boolean;
  onCancelar: () => void;
  onSalvar: (dados: any) => void;
  onExcluir?: () => void;
}) {
  const [nome, setNome] = useState(regua?.nome || '');
  const [gatilho, setGatilho] = useState(regua?.gatilho || GATILHOS_SUPORTADOS[0]!.v);
  const [template, setTemplate] = useState(regua?.mensagem_template || '');

  const submeter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim() || !template.trim()) {
      toast('Nome e mensagem são obrigatórios.');
      return;
    }
    if (!/\bSAIR\b/i.test(template)) {
      toast('A mensagem precisa informar como o contato pode sair (ex: "envie SAIR").');
      return;
    }
    onSalvar(
      regua
        ? { nome: nome.trim(), mensagem_template: template.trim() }
        : { nome: nome.trim(), gatilho, mensagem_template: template.trim(), ativa: false },
    );
  };

  return (
    <form onSubmit={submeter}>
      <Drawer
        aberto
        titulo={regua ? 'Editar régua' : 'Nova régua de relacionamento'}
        aoFechar={onCancelar}
        rodape={
          <>
            {onExcluir && (
              <button
                type="button"
                className="btn"
                style={{ color: 'var(--crit)', marginRight: 'auto' }}
                onClick={onExcluir}
                disabled={excluindo}
              >
                Excluir
              </button>
            )}
            <button type="button" className="btn" onClick={onCancelar}>
              Cancelar
            </button>
            <button type="submit" className="btn pri" disabled={salvando}>
              {salvando ? 'Salvando...' : regua ? 'Salvar alterações' : 'Criar régua'}
            </button>
          </>
        }
      >
        <div className="field">
          <label>Nome *</label>
          <input
            className="in"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Ex: Mensagem de aniversário"
            required
          />
        </div>

        <div className="field">
          <label>Gatilho *</label>
          <select
            className="in"
            value={gatilho}
            disabled={!!regua}
            onChange={(e) => setGatilho(e.target.value)}
          >
            {GATILHOS_SUPORTADOS.map((g) => (
              <option key={g.v} value={g.v}>
                {g.l} · {g.v}
              </option>
            ))}
          </select>
          <small style={{ fontSize: 11, color: 'var(--ink3)' }}>
            {regua
              ? 'O gatilho não muda depois de criada: trocá-lo mudaria o significado dos envios já registrados.'
              : 'Só aparecem os eventos que a plataforma efetivamente emite hoje.'}
          </small>
        </div>

        <div className="field">
          <label>Mensagem *</label>
          <textarea
            className="in"
            style={{ minHeight: 110 }}
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            placeholder="Use {nome} para o primeiro nome. Inclua a saída: 'Para não receber mais, envie SAIR.'"
            required
          />
          <small style={{ fontSize: 11, color: 'var(--ink3)' }}>
            A mensagem precisa conter a instrução de descadastro: é exigência de LGPD e da
            política de mensageria.
          </small>
        </div>

        {template && (
          <div className="wa">
            <div className="bub" style={{ fontSize: 12 }}>
              {template.replace('{nome}', 'Maria')}
            </div>
          </div>
        )}

        {regua && (
          <>
            <dl className="dl" style={{ marginTop: 12 }}>
              <dt>Público</dt>
              <dd>{publicoDe(regua)}</dd>
              <dt>30 dias</dt>
              {/* Ver comentário no KPI "Mensagens automáticas · 30d": não há
                  endpoint que agregue envios/respostas por régua hoje. */}
              <dd>não medido; a API não expõe envios por régua</dd>
            </dl>

            {/* Guard-rails (proto:819, DRAWERS.regua). O protótipo crava
                "3 camadas ✓" e "bloqueia sozinha" como se fossem garantias
                medidas; aqui só afirmamos o que o código de fato faz hoje —
                ver SolicitacoesService.triggerFeedbackRegua (único disparo
                real, checa base_legal==='optin') e CampanhasService.create
                (defeso eleitoral só para campanhas, e não exposto por API). */}
            <div className="card flat" style={{ marginTop: 12 }}>
              <h3 style={{ marginTop: 0 }}>Guard-rails</h3>
              <dl className="dl" style={{ marginTop: 6 }}>
                <dt>Opt-in</dt>
                <dd className="ok">verificado antes de cada envio</dd>
                <dt>Teto</dt>
                <dd>
                  {config?.teto_mensagens_mes ?? '—'}/mês somando réguas
                </dd>
                <dt>Silêncio</dt>
                <dd>
                  {config?.silencio_inicio ?? '—'}–{config?.silencio_fim ?? '—'}
                </dd>
                <dt>Defeso eleitoral</dt>
                <dd>indisponível; a API não expõe o status</dd>
                <dt>Auto-pausa</dt>
                <dd>não implementada</dd>
              </dl>
            </div>
          </>
        )}

      </Drawer>
    </form>
  );
}
